// booking/hooks/usePricingPreview.js

import { useCallback, useRef, useState } from 'react';
import { pricingApi } from '../services/booking.api';
import { stableStringify } from '../utils/object';

export function usePricingPreview({
    values,
    setValues,
    isHydrating,
    setInsuranceQuotes,
}) {
    const reqIdRef = useRef(0);
    const lastAppliedHashRef = useRef("");

    const [isPricingLoading, setIsPricingLoading] = useState(false);
    const [needsRecalc, setNeedsRecalc] = useState(false);
    const [lastError, setLastError] = useState(null);

    // ===============================
    // 标记定价需要重新计算
    // ===============================
    const markDirtyPricing = useCallback(() => {
        if (isHydrating) return;
        setNeedsRecalc(true);
    }, [isHydrating]);

    // ===============================
    // 构建 pricing payload
    // ===============================
    const buildPayload = useCallback(() => {
        const shopId = values?.shopSnapshot?.pickup?.shop?.id || "";
        const shopMotorcycleId =
            values?.motorcycleSnapshot?.shopMotorcycle?.id || "";

        const startDate = values?.rentalOrder?.startDate || null;
        const endDate = values?.rentalOrder?.endDate || null;
        const startTime = values?.rentalOrder?.startTime || "";
        const endTime = values?.rentalOrder?.endTime || "";

        const supplierCurrencyCode =
            values?.pricingSnapshot?.calculation?.supplierCurrencyCode || "";

        const customerCurrencyCode =
            values?.pricingSnapshot?.calculation?.customerCurrencyCode || "";

        const discountLocks =
            values?.pricingSnapshot?.calculation?.discountLocks ?? {
                rmbDiscountLocked: false,
                supplierDiscountLocked: false,
            };

        const rmbDiscountCents =
            Number(values?.invoiceSnapshot?.calculation?.rmbDiscountCents ?? 0);

        const supplierDiscountCents =
            Number(values?.invoiceSnapshot?.calculation?.supplierDiscountCents ?? 0);

        const discountOverrides = {
            ...(discountLocks.rmbDiscountLocked
                ? { rmbDiscountCents }
                : {}),
            ...(discountLocks.supplierDiscountLocked
                ? { supplierDiscountCents }
                : {}),
        };

        const rentalOrderExtras = (values?.rentalOrderExtra ?? []).map((x) => ({
            shopMotorcycleExtraId: x?.shopMotorcycleExtraId ?? undefined,
            quantity: Number(x?.quantity ?? 1),
            name: x?.name ?? '',
            description: x?.description ?? '',
            priceWithFeeCents: Number(x?.priceCents ?? 0),
        }));

        const selectedInsuranceIds =
            (values?.rentalOrderInsurance ?? [])
                .map((x) => x?.insuranceId)
                .filter(Boolean);

        const countryISOCode =
            values?.customerSnapshot?.country?.ISOCode ||
            values?.shopSnapshot?.pickup?.location?.country?.ISOCode ||
            "";

        return {
            shopId,
            shopMotorcycleId,
            startDate,
            endDate,
            startTime,
            endTime,
            supplierCurrencyCode,
            customerCurrencyCode,
            discountLocks,
            discountOverrides,
            rentalOrderExtras,
            selectedInsuranceIds,
            countryISOCode,
        };
    }, [values]);

    // ===============================
    // 执行重新计算
    // ===============================
    const recalculate = useCallback(async () => {
        if (isHydrating) return;

        const payload = buildPayload();

        if (
            !payload.shopId ||
            !payload.shopMotorcycleId ||
            !payload.startDate ||
            !payload.endDate
        ) {
            setLastError("Missing required fields (shop/motorcycle/date).");
            return;
        }

        const reqId = ++reqIdRef.current;
        setIsPricingLoading(true);
        setLastError(null);

        try {
            const res = await pricingApi.preview(payload);
            if (reqId !== reqIdRef.current) return;

            const hash = stableStringify(res);
            if (hash === lastAppliedHashRef.current) {
                setNeedsRecalc(false);
                return;
            }

            lastAppliedHashRef.current = hash;

            const calcRes = res?.result?.calculation || {};

            setValues((prev) => {
                const next = { ...prev };

                next.pricingSnapshot = next.pricingSnapshot || {};
                next.pricingSnapshot.calculation = {
                    ...(next.pricingSnapshot.calculation || {}),
                    ...calcRes,
                };

                if (res?.inputs?.days != null) {
                    next.pricingSnapshot.calculation.days = res.inputs.days;
                }

                next.invoiceSnapshot = next.invoiceSnapshot || {};
                const prevInvoiceCalc = next.invoiceSnapshot.calculation || {};

                next.invoiceSnapshot.calculation = {
                    ...prevInvoiceCalc,
                    commissionCents:
                        calcRes.commissionTotalCents ??
                        prevInvoiceCalc.commissionCents ??
                        0,
                    managementFeeCents:
                        calcRes.managementFeeTotalCents ??
                        prevInvoiceCalc.managementFeeCents ??
                        0,
                    rmbDiscountCents:
                        calcRes.rmbDiscountCents ??
                        prevInvoiceCalc.rmbDiscountCents ??
                        0,
                    supplierDiscountCents:
                        calcRes.supplierDiscountCents ??
                        prevInvoiceCalc.supplierDiscountCents ??
                        0,
                };

                next.pricingSnapshot.lastPricingInputs =
                    res?.inputs || payload;

                return next;
            });

            if (Array.isArray(res?.insuranceQuotes)) {
                setInsuranceQuotes(res.insuranceQuotes);
            }

            setNeedsRecalc(false);
        } catch (e) {
            if (reqId !== reqIdRef.current) return;

            setLastError(
                e?.response?.data?.message ||
                e?.message ||
                "Pricing preview failed"
            );
        } finally {
            if (reqId === reqIdRef.current) {
                setIsPricingLoading(false);
            }
        }
    }, [buildPayload, isHydrating, setValues, setInsuranceQuotes]);

    return {
        isPricingLoading,
        needsRecalc,
        lastError,
        markDirtyPricing,
        recalculate,
    };
}